import { useCallback, useState } from 'react';
import { useEffect } from 'react';
import { Country, InputRuleApiModel, MarketplaceOrderApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { GenerateNumberOptions } from '../../lib/helpers/ArrayHelper';
import Conversion from '../currency/Conversion';
import { TariffCode } from '../customs/TariffCode';
import { TariffSelector } from '../customs/TariffSelector';
import { TariffSuggestions } from '../customs/TariffSuggestions';
import { ProductImage } from '../product/ProductImage';
import { GetCustomerReferenceNumber } from './item/OrderReferenceNumberUtils';
import P2GConditionalImage from '../P2GConditionalImage';
import { CurrencyConversion } from './CurrencyConversion';
import { CurrencyConversion as CurrencyConversionKey } from '../../lib/consts/ItemMetaConstants';
import {
  P2GBox,
  P2GGrid,
  P2GText,
  P2GChip,
  P2GLink,
  P2GTextField,
  P2GDropdown,
  P2GRadio,
  P2GButton
} from '@parcel2go/core-ui';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';
import conditionalIsRequired from '../../lib/helpers/CustomerConditionalHelper';

interface props {
  onUpdate?: (item: MarketplaceOrderApiModel) => void;
  marketplaceOrder: MarketplaceOrderApiModel;
  countries: Country[];
  productValidation: InputRuleApiModel[];
  tariffSystem: string;
  currencyCode: string;
  showReferenceNumber: boolean;
  item: SmartSendItemApiModel;
  onOpen?: () => void;
  startMinimised?: boolean;
}

export const ContentsSelectionRow: React.FC<props> = (props) => {
  const [unit, setUnit] = useState<'UNIT' | 'TOTAL'>(props.marketplaceOrder.Quantity === 1 ? 'UNIT' : 'TOTAL');
  const [value, setValue] = useState<string>(props.marketplaceOrder.Value as any);
  const [codeOnLoad, setCodeOnLoad] = useState(!!props.marketplaceOrder.Product.TariffCode);
  const [minimised, setMinimised] = useState(props.startMinimised ?? true);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const tariffCodeRequirement = props.productValidation?.find((x) => x.name === 'TariffCode');
  const countryOfManufactureRequirement = props.productValidation?.find((x) => x.name === 'CountryOfManufacture');

  const conditionalIsRequiredCallback = useCallback(
    (m: InputRuleApiModel) => conditionalIsRequired(m, 'Sender', props.item.Sender, props.item.Recipient),
    [props.item.Sender, props.item.Recipient]
  );

  useEffect(() => {
    if (unit === 'TOTAL') {
      setValue(props.marketplaceOrder.Value as any);
    } else {
      setValue((props.marketplaceOrder.Value / props.marketplaceOrder.Quantity) as any);
    }
  }, [unit, props.marketplaceOrder.Value, props.marketplaceOrder.Quantity]);

  const updateValue = (v: string) => {
    setValue(v);
    props.onUpdate?.({ ...props.marketplaceOrder, Value: getValue(Number(v), props.marketplaceOrder.Quantity) });
  };

  const updateProductDetails = (prop: string, v: string) => {
    const updatedProduct = { ...props.marketplaceOrder.Product };
    if (Object.keys(props.marketplaceOrder.Product).includes(prop)) {
      updatedProduct[prop] = v;
    } else {
      updatedProduct.Meta = { ...updatedProduct.Meta, [prop]: { Value: v, Type: 'String' } };
    }
    const updated = { ...props.marketplaceOrder, Product: updatedProduct };
    props.onUpdate?.({ ...updated, Value: getValue(value as any, updated.Quantity) });
  };

  const updateItemDetails = (prop: string, v: any) => {
    const updated = { ...props.marketplaceOrder };
    updated[prop] = v;
    props.onUpdate?.({ ...updated, Value: getValue(value as any, updated.Quantity) });
  };

  const getValue = (v: number, q: number): any => {
    if (unit === 'TOTAL') {
      return v;
    }
    return v * q;
  };

  return (
    <P2GBox classNames={'p2g-marketplace-item'}>
      {minimised ? (
        <P2GBox
          onClick={() => {
            setMinimised(false);
            props.onOpen?.();
          }}
        >
          <P2GGrid container gap={2} alignItems="center" alignContent="center" wrap="nowrap">
            {props.marketplaceOrder.Product.ImageUrl && (
              <P2GGrid item flexShrink={1}>
                <P2GBox bordered textAlign="center">
                  <div
                    style={{
                      overflow: 'hidden',
                      height: '55px',
                      lineHeight: '55px',
                      border: '5px solid white',
                      width: '75px'
                    }}
                  >
                    <P2GConditionalImage
                      style={{ height: '55px' }}
                      src={props.marketplaceOrder.Product.ImageUrl}
                      alt="Image"
                    />
                  </div>
                </P2GBox>
              </P2GGrid>
            )}
            <P2GGrid item flexShrink={1}>
              <P2GText>
                <strong>{props.marketplaceOrder.Quantity} x</strong>
              </P2GText>
            </P2GGrid>
            <P2GGrid item flexGrow={1}>
              <div>
                <P2GGrid container spacing={1} alignItems="center">
                  <P2GGrid item xs={12}>
                    <P2GText variant="body2">
                      <strong>{props.marketplaceOrder.Product.Description}</strong>
                      <br />
                      <Trans
                        t={t}
                        i18nKey="CONTENTS_TOTAL_VALUE"
                        values={{ value: toDisplayCurrency(props.currencyCode, props.marketplaceOrder.Value) }}
                        defaults="Total Value: {{ value }}"
                      />
                      {props?.marketplaceOrder?.Meta?.[CurrencyConversionKey]?.Value && (
                        <>
                          <br />
                          <CurrencyConversion
                            ValueJSON={props?.marketplaceOrder?.Meta?.[CurrencyConversionKey].Value}
                          />
                        </>
                      )}
                    </P2GText>
                  </P2GGrid>
                  {props.showReferenceNumber && GetCustomerReferenceNumber(props.marketplaceOrder) && (
                    <P2GGrid item>
                      <P2GChip
                        size="small"
                        color="primary"
                        label={GetCustomerReferenceNumber(props.marketplaceOrder)}
                      />
                    </P2GGrid>
                  )}
                  {props.marketplaceOrder.Product.Sku && (
                    <P2GGrid item>
                      <P2GChip size="small" color="info" label={props.marketplaceOrder.Product.Sku} />
                    </P2GGrid>
                  )}
                </P2GGrid>
              </div>
            </P2GGrid>
            <P2GGrid item flexShrink={1}>
              <P2GLink>{t('CONTENTS_EDIT_LINK', { defaultValue: 'Edit' })}</P2GLink>
            </P2GGrid>
          </P2GGrid>
        </P2GBox>
      ) : (
        <>
          <P2GGrid container gap={2} alignItems="center" wrap="nowrap" mb={2}>
            {props.marketplaceOrder.Product.ImageUrl && (
              <P2GGrid item flexShrink={1}>
                <ProductImage
                  p={0}
                  product={props.marketplaceOrder.Product}
                  style={{ maxWidth: '110px', maxHeight: '40px' }}
                />
              </P2GGrid>
            )}
            <P2GGrid item flexGrow={1}>
              <P2GTextField
                fullWidth
                showErrorsWhenPristine={true}
                value={props.marketplaceOrder.Product.Description || ''}
                onChange={(v) => updateProductDetails('Description', v.currentTarget.value)}
                label={`${props.marketplaceOrder.Source.Name} Item`}
                id={`Description`}
                required
              />
            </P2GGrid>
          </P2GGrid>
          <P2GGrid container spacing={2} alignItems="center">
            <P2GGrid item xs={6} sm={3}>
              <P2GDropdown
                fullWidth
                value={props.marketplaceOrder.Quantity as any}
                options={GenerateNumberOptions(1, 100)}
                label={t('QUANTITY_INPUT_LABEL', { defaultValue: 'Quantity' })}
                id={`Quantity`}
                onChange={(v) => updateItemDetails('Quantity', v)}
              />
            </P2GGrid>
            <P2GGrid item xs={6} sm={4}>
              <P2GTextField
                fullWidth
                startAdornment={
                  <Conversion
                    value={Number(value)}
                    currency={props.currencyCode}
                    updateValue={updateValue}
                    title={t('ITEM_VALUE_SIDEPANEL_TITLE', { defaultValue: 'Item Value' })}
                  />
                }
                value={value}
                onChange={(v) => updateValue(v.currentTarget.value)}
                label={t('VALUE_INPUT_LABEL', { defaultValue: 'Value' })}
                showErrorsWhenPristine={true}
                id={`Value`}
              />
            </P2GGrid>
            <P2GGrid item xs={12} sm={5}>
              <P2GTextField
                fullWidth
                showErrorsWhenPristine={true}
                value={props.marketplaceOrder.Product?.Sku || ''}
                onChange={(v) => updateProductDetails('Sku', v.currentTarget.value)}
                label={t('SKU_INPUT_LABEL', { defaultValue: 'SKU' })}
                id={`SKU`}
              />
            </P2GGrid>
            {props.marketplaceOrder.Quantity > 1 && (
              <P2GGrid item xs={12}>
                <P2GRadio
                  value="UNIT"
                  label={t('UNIT_COST_RADIO_BUTTON', { defaultValue: 'Unit Cost' })}
                  onChange={() => setUnit('UNIT')}
                  name="Unit"
                  id="Unit"
                  disabled={props.marketplaceOrder.Quantity === 1}
                  checked={unit === 'UNIT'}
                />

                <P2GRadio
                  value="TOTAL"
                  label={t('TOTAL_COST_RADIO_BUTTON', { defaultValue: 'Total Cost' })}
                  onChange={() => setUnit('TOTAL')}
                  name="Unit"
                  id="Total"
                  disabled={props.marketplaceOrder.Quantity === 1}
                  checked={unit === 'TOTAL'}
                />
              </P2GGrid>
            )}
          </P2GGrid>
        </>
      )}
      <P2GGrid container gap={2} mt={2} alignItems="center">
        {countryOfManufactureRequirement && (
          <P2GGrid item xs={12}>
            <P2GDropdown
              key={`${props.countries.length}`}
              id="Manufactured In"
              label={t('MANUFACTURED_IN_INPUT_LABEL', { defaultValue: 'Manufactured In' })}
              onChange={(v) => updateProductDetails('CountryOfManufacture', String(v))}
              options={props.countries.map((x) => {
                return { label: x.LocalisedName, value: x.Name };
              })}
              required={
                conditionalIsRequiredCallback(countryOfManufactureRequirement) ??
                countryOfManufactureRequirement?.required
              }
              value={props.marketplaceOrder.Product.CountryOfManufacture || ''}
            />
          </P2GGrid>
        )}
        {tariffCodeRequirement && (
          <>
            <P2GGrid item xs={12}>
              {codeOnLoad ? (
                <P2GGrid container spacing={1} alignItems="center">
                  <P2GGrid item flexShrink={1}>
                    <TariffCode code={props.marketplaceOrder.Product.TariffCode || ''} selectable={true} />
                  </P2GGrid>
                  <P2GGrid item flexShrink={1}>
                    <P2GLink onClick={() => setCodeOnLoad(false)} variant="body2">
                      {t('EDIT_TARIFF_CODE_LINK', { defaultValue: 'Edit Tariff Code' })}
                    </P2GLink>
                  </P2GGrid>
                </P2GGrid>
              ) : (
                <P2GTextField
                  fullWidth
                  showErrorsWhenPristine={true}
                  required={conditionalIsRequiredCallback(tariffCodeRequirement) ?? tariffCodeRequirement?.required}
                  value={props.marketplaceOrder.Product.TariffCode || ''}
                  onChange={(v) => updateProductDetails('TariffCode', v.currentTarget.value)}
                  label={t('TARIFF_CODE_INPUT_LABEL', { defaultValue: 'Tariff Code' })}
                  id={`TariffCode`}
                  endAdornment={
                    <TariffSelector
                      title={t('TARIFF_CODE_SELECTOR_SIDEPANEL_TITLE', { defaultValue: 'Tariff Code Selector' })}
                      default="SUGGEST"
                      options={['SUGGEST', 'SEARCH', 'BROWSE']}
                      control={
                        <P2GButton variant="primary">{t('BROWSE_TARIFF_BUTTON', { defaultValue: 'Browse' })}</P2GButton>
                      }
                      searchTerm={props.marketplaceOrder.Product.Description}
                      system={props.tariffSystem}
                      tariffCode={props.marketplaceOrder.Product.TariffCode || ''}
                      canSelect={true}
                      selectCode={(v) => {
                        if (v?.length >= 6) {
                          setCodeOnLoad(true);
                        }
                        updateProductDetails('TariffCode', v);
                      }}
                    />
                  }
                />
              )}
            </P2GGrid>
            {!codeOnLoad && !(props.marketplaceOrder.Product.TariffCode || '') && (
              <P2GGrid item xs={12}>
                <TariffSuggestions
                  title={t('TARIFF_SUGGESTION_TITLE', { defaultValue: 'Suggestion:' })}
                  canSelect={true}
                  limit={3}
                  selectCode={(v) => updateProductDetails('TariffCode', v)}
                  tariffSystem={props.tariffSystem}
                  itemDescription={props.marketplaceOrder.Product.Description}
                />
              </P2GGrid>
            )}
          </>
        )}
      </P2GGrid>
    </P2GBox>
  );
};
